// ⌘K "Ask AI" (docs/specs/ai-import.md §3). The palette renders <AskAiCommand> when the query reads
// like a sentence. Selecting it opens a dialog that asks the server for a PLAN (POST
// /api/admin/ai/command — the server never executes anything), previews what each step would touch
// through the normal admin APIs, and runs the steps through those same APIs only after the
// merchant confirms — so permissions, validation, re-auth and audit all apply. Price and status
// changes can be undone right after.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { matchPath, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight, Ban, Check, CircleAlert, ExternalLink, FileText, Loader2, Megaphone, Navigation, Package, Percent, Search, Sparkles, Tag, Undo2, Wallet,
} from 'lucide-react'
import { routes } from '@/app/routes'
import { api, ApiError, qs } from '@/lib/api'
import { useSession } from '@/lib/session'
import { money } from '@/lib/format'
import {
  Badge, Banner, Button, Checkbox, CommandGroup, CommandItem, Dialog, Kbd, Skeleton, cn, errorMessage, toast, withReauth,
} from '@/components/ui'
import type { Permission } from '@shared/types'

// ─── Plan shape (server: src/ai/command.ts) ───────────────────────────────────────────────
interface Filter { collectionId: string | null; tag: string | null; productIds: string[] | null }
type Step =
  | { id: string; action: 'navigate'; description: string; params: { path: string } }
  | { id: string; action: 'create_discount'; description: string; params: { title: string; code: string | null; kind: 'order' | 'product' | 'shipping'; valueType: 'percent' | 'fixed'; value: number; appliesTo: { type: 'all' | 'collections' | 'products'; ids: string[] }; startsAt: string | null; endsAt: string | null } }
  | { id: string; action: 'bulk_price_change'; description: string; params: { filter: Filter; change: { type: 'percent' | 'fixed'; value: number; field: 'price' | 'compareAtPrice' } } }
  | { id: string; action: 'set_product_status'; description: string; params: { filter: Filter; status: 'active' | 'draft' | 'archived' } }
  | { id: string; action: 'create_product_draft'; description: string; params: { title: string; description: string; price: number; options: Array<{ name: string; values: string[] }> } }
  | { id: string; action: 'search'; description: string; params: { entity: 'orders' | 'products' | 'customers'; filters: Record<string, string | string[] | boolean> } }
  | { id: string; action: 'toggle_payment_method'; description: string; params: { provider: string; enabled: boolean } }
  | { id: string; action: 'update_announcement'; description: string; params: { text: string } }
interface Plan { summary: string; steps: Step[]; warnings: string[]; credits?: number }

const ACTIONS = new Set(['navigate', 'create_discount', 'bulk_price_change', 'set_product_status', 'create_product_draft', 'search', 'toggle_payment_method', 'update_announcement'])
function isPlan(v: unknown): v is Plan {
  const p = v as Plan
  return !!p && typeof p.summary === 'string' && Array.isArray(p.steps) && Array.isArray(p.warnings)
    && p.steps.every(s => !!s && typeof s.id === 'string' && ACTIONS.has(s.action) && !!s.params && typeof s.params === 'object')
}

const ICON: Record<Step['action'], ReactNode> = {
  navigate: <Navigation />, create_discount: <Percent />, bulk_price_change: <Tag />, set_product_status: <Package />,
  create_product_draft: <FileText />, search: <Search />, toggle_payment_method: <Wallet />, update_announcement: <Megaphone />,
}
const PERM: Record<Step['action'], Permission | null> = {
  navigate: null, create_discount: 'discounts', bulk_price_change: 'products', set_product_status: 'products', create_product_draft: 'products',
  search: null, toggle_payment_method: 'payments', update_announcement: 'online_store',
}
const ENTITY_PERM: Record<string, Permission> = { orders: 'orders', products: 'products', customers: 'customers' }
const LIST_PATH: Record<string, string> = { orders: '/orders', products: '/products', customers: '/customers' }

/** Admin paths only (real routes from app/routes.tsx, never an external URL). */
export function isAdminPath(path: string): boolean {
  if (!/^\/(?!\/)/.test(path) || /[\s\\]/.test(path)) return false
  const pathname = path.split('?')[0]
  return routes.some(r => r.layout !== 'public' && r.layout !== 'raw' && !!matchPath({ path: r.path, end: true }, pathname))
}

// ─── Minor-unit helpers (store currency) ──────────────────────────────────────────────────
const DECIMALS: Record<string, number> = { JPY: 0, KRW: 0, VND: 0, CLP: 0, UGX: 0, RWF: 0, XOF: 0, XAF: 0, KWD: 3, BHD: 3, OMR: 3, JOD: 3, TND: 3 }
const toMinor = (major: number, currency: string) => Math.round(major * 10 ** (DECIMALS[currency] ?? 2))
const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!))

interface VariantRow { id: string; productId: string; productTitle: string; title: string; price: number; compareAtPrice: number | null }
const isVariantRow = (v: any): v is VariantRow => !!v && typeof v.id === 'string' && typeof v.price === 'number'

function filterParams(f: Filter) {
  return { collection: f.collectionId ?? undefined, tag: f.tag ?? undefined, ids: f.productIds?.length ? f.productIds.join(',') : undefined }
}
async function allVariants(f: Filter, max = 10_000): Promise<VariantRow[]> {
  const out: VariantRow[] = []
  let cursor: string | undefined
  for (let i = 0; i < 20 && out.length < max; i++) {
    const r = await api.get<{ items: unknown[]; nextCursor: string | null }>(`/api/admin/variants${qs({ ...filterParams(f), limit: 1000, cursor })}`)
    if (!r || !Array.isArray(r.items)) throw new ApiError(500, 'bad_response', 'Unexpected response from the variants list.')
    out.push(...r.items.filter(isVariantRow))
    if (!r.nextCursor) break
    cursor = r.nextCursor
  }
  return out
}
function repriced(v: VariantRow, c: { type: 'percent' | 'fixed'; value: number; field: 'price' | 'compareAtPrice' }, currency: string): { price?: number; compareAtPrice?: number | null } | null {
  const apply = (x: number) => Math.max(0, c.type === 'percent' ? Math.round(x * (1 + c.value / 100)) : x + toMinor(c.value, currency))
  if (c.field === 'price') {
    const price = apply(v.price)
    if (price === v.price) return null
    return v.compareAtPrice !== null && price >= v.compareAtPrice ? { price, compareAtPrice: null } : { price }
  }
  if (v.compareAtPrice === null) return null
  const cmp = apply(v.compareAtPrice)
  return { compareAtPrice: cmp > v.price ? cmp : null }
}

function searchQuery(s: Extract<Step, { action: 'search' }>): string {
  const p: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(s.params.filters)) p[k] = Array.isArray(v) ? v.join(',') : typeof v === 'boolean' ? String(v) : v
  return qs(p)
}
function describeFilter(f: Filter, collections: Map<string, string>): string {
  if (f.productIds?.length) return `${f.productIds.length} named product${f.productIds.length === 1 ? '' : 's'}`
  const parts = [f.collectionId ? `in “${collections.get(f.collectionId) ?? 'collection'}”` : '', f.tag ? `tagged “${f.tag}”` : ''].filter(Boolean)
  return parts.length ? `products ${parts.join(' and ')}` : 'every product'
}

// ─── Preview + execution per step ─────────────────────────────────────────────────────────
interface Preview { lines: ReactNode[]; blocked?: string; warn?: string }
interface Outcome { status: 'pending' | 'running' | 'done' | 'error' | 'skipped'; message?: string; link?: { to: string; label: string }; undo?: () => Promise<void> }

interface Ctx { currency: string; role: string; can: (p: Permission) => boolean; collections: Map<string, string> }

async function preview(s: Step, x: Ctx): Promise<Preview> {
  const perm = s.action === 'search' ? ENTITY_PERM[s.params.entity] : PERM[s.action]
  if (perm && !x.can(perm)) return { lines: [], blocked: 'Your staff account doesn’t have access to this area.' }
  switch (s.action) {
    case 'navigate':
      return isAdminPath(s.params.path) ? { lines: [<>Opens <code className="rounded bg-surface-sunken px-1 text-sm">{s.params.path}</code></>] } : { lines: [], blocked: 'That page doesn’t exist.' }
    case 'create_discount': {
      const p = s.params
      const amount = p.valueType === 'percent' ? `${p.value}% off` : `${money(toMinor(p.value, x.currency), x.currency)} off`
      const scope = p.kind === 'shipping' ? 'shipping' : p.appliesTo.type === 'all' ? (p.kind === 'order' ? 'the whole order' : 'all products')
        : p.appliesTo.type === 'collections' ? p.appliesTo.ids.map(id => `“${x.collections.get(id) ?? 'collection'}”`).join(', ') : `${p.appliesTo.ids.length} products`
      const when = p.endsAt ? `until ${new Date(p.endsAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}` : 'no end date'
      return { lines: [<><strong className="font-medium text-text">{amount}</strong> {scope}</>, <>{p.code ? <>Code <Kbd>{p.code}</Kbd></> : 'Applied automatically'} · {p.startsAt ? `starts ${new Date(p.startsAt).toLocaleDateString()}` : 'starts now'} · {when}</>] }
    }
    case 'bulk_price_change': {
      const vs = await allVariants(s.params.filter)
      const changes = vs.map(v => ({ v, n: repriced(v, s.params.change, x.currency) })).filter(c => c.n)
      const products = new Set(changes.map(c => c.v.productId)).size
      const cleared = changes.filter(c => c.n!.compareAtPrice === null && c.v.compareAtPrice !== null).length
      if (!changes.length) return { lines: ['No prices would change.'], blocked: 'Nothing to change.' }
      return {
        lines: [
          <><strong className="font-medium text-text">{changes.length.toLocaleString()} variant{changes.length === 1 ? '' : 's'}</strong> across {products.toLocaleString()} product{products === 1 ? '' : 's'} ({describeFilter(s.params.filter, x.collections)})</>,
          ...changes.slice(0, 4).map(({ v, n }) => (
            <span key={v.id} className="flex items-center gap-2"><span className="truncate">{v.productTitle}{v.title && v.title !== 'Default' ? ` · ${v.title}` : ''}</span>
              <span className="ml-auto shrink-0 tabular text-text-subtle line-through">{money(s.params.change.field === 'price' ? v.price : v.compareAtPrice, x.currency)}</span>
              <ArrowRight className="size-3 shrink-0 text-text-subtle" />
              <span className="shrink-0 tabular font-medium text-text">{money(s.params.change.field === 'price' ? n!.price! : n!.compareAtPrice ?? null, x.currency)}</span></span>
          )),
        ],
        warn: cleared ? `${cleared} variant${cleared === 1 ? '' : 's'} would lose their compare-at price (the new price isn’t lower anymore).` : undefined,
      }
    }
    case 'set_product_status': {
      const r = await api.get<{ items: unknown[]; total?: number }>(`/api/admin/products${qs({ ...filterParams(s.params.filter), limit: 5 })}`)
      const total = typeof r?.total === 'number' ? r.total : Array.isArray(r?.items) ? r.items.length : 0
      if (!total) return { lines: ['No products match.'], blocked: 'Nothing to change.' }
      const names = (Array.isArray(r.items) ? r.items : []).map((p: any) => (typeof p?.title === 'string' ? p.title : '')).filter(Boolean)
      return { lines: [<><strong className="font-medium text-text">{total.toLocaleString()} product{total === 1 ? '' : 's'}</strong> → {s.params.status} ({describeFilter(s.params.filter, x.collections)})</>, ...(names.length ? [names.slice(0, 4).join(', ') + (total > 4 ? '…' : '')] : [])] }
    }
    case 'create_product_draft': {
      const p = s.params
      const variants = p.options.reduce((n, o) => n * Math.max(1, o.values.length), 1)
      return { lines: [<><strong className="font-medium text-text">{p.title}</strong> · {money(toMinor(p.price, x.currency), x.currency)}</>, p.options.length ? `${p.options.map(o => `${o.name}: ${o.values.join(', ')}`).join(' · ')} (${variants} variants)` : 'Single variant · saved as a draft'] }
    }
    case 'search': {
      const r = await api.get<{ items: any[]; total?: number }>(`/api/admin/${s.params.entity}${searchQuery(s)}${searchQuery(s) ? '&' : '?'}limit=5`)
      const items = Array.isArray(r?.items) ? r.items : []
      const total = typeof r?.total === 'number' ? r.total : items.length
      const label = (it: any) => s.params.entity === 'orders' ? `${it?.name ?? ''} · ${it?.customer?.name ?? it?.email ?? ''}`
        : s.params.entity === 'products' ? String(it?.title ?? '') : String(it?.name ?? it?.email ?? it?.phone ?? '')
      return { lines: [<><strong className="font-medium text-text">{total.toLocaleString()}</strong> {s.params.entity} match</>, ...items.slice(0, 4).map((it, i) => <span key={i} className="truncate">{label(it)}</span>)] }
    }
    case 'toggle_payment_method': {
      if (x.role === 'staff') return { lines: [], blocked: 'Only the store owner or an admin can change payment methods.' }
      const r = await api.get<{ configured: any[] }>('/api/admin/payments/providers')
      const row = (Array.isArray(r?.configured) ? r.configured : []).find(p => p?.provider === s.params.provider)
      if (!row) return { lines: [], blocked: 'This payment method isn’t set up yet.' }
      if (row.enabled === s.params.enabled) return { lines: [`${row.displayName || row.label} is already ${row.enabled ? 'on' : 'off'}.`], blocked: 'Nothing to change.' }
      return { lines: [<>{row.displayName || row.label}: {row.enabled ? 'on' : 'off'} → <strong className="font-medium text-text">{s.params.enabled ? 'on' : 'off'}</strong></>], warn: s.params.enabled && row.status === 'needs_setup' ? 'It still needs setup — turning it on may fail.' : undefined }
    }
    case 'update_announcement': {
      const t = await headerTemplate()
      const current = announcementText(t.hasDraft && t.draftDoc ? t.draftDoc : t.doc)
      if (current === null) return { lines: [], blocked: 'Your header has no announcement bar. Add one in the theme editor first.' }
      return { lines: [<>“{current || 'empty'}” → <strong className="font-medium text-text">“{s.params.text}”</strong></>], warn: t.hasDraft ? 'The header has unpublished edits, so this is saved as a draft for you to publish.' : undefined }
    }
  }
}

interface TemplateDto { id: string; updatedAt: string; hasDraft: boolean; doc: { version: 1; nodes: any[] }; draftDoc: { version: 1; nodes: any[] } | null }
async function headerTemplate(): Promise<TemplateDto> {
  const themes = await api.get<{ publishedThemeId: string | null }>('/api/admin/online-store/themes')
  if (!themes?.publishedThemeId) throw new ApiError(404, 'not_found', 'No published theme.')
  const list = await api.get<{ items: Array<{ id: string; kind: string; handle: string }> }>(`/api/admin/online-store/themes/${themes.publishedThemeId}/templates`)
  const header = (Array.isArray(list?.items) ? list.items : []).find(t => t.kind === 'header' && t.handle === '')
  if (!header) throw new ApiError(404, 'not_found', 'No header template.')
  const t = await api.get<TemplateDto>(`/api/admin/online-store/templates/${header.id}`)
  if (!t || !t.doc || !Array.isArray(t.doc.nodes)) throw new ApiError(500, 'bad_response', 'Unexpected template response.')
  return t
}
function walk(nodes: any[], fn: (n: any) => void) { for (const n of nodes ?? []) { fn(n); if (Array.isArray(n?.children)) walk(n.children, fn) } }
function announcementText(doc: { nodes: any[] }): string | null {
  let text: string | null = null
  walk(doc.nodes, n => { if (text === null && n?.type === 'announcement-bar') text = String(n.props?.messages?.[0]?.text ?? '') })
  return text
}

async function execute(s: Step, x: Ctx & { qc: ReturnType<typeof useQueryClient> }): Promise<Outcome> {
  switch (s.action) {
    case 'navigate': return { status: 'done', link: { to: s.params.path, label: 'Open' } }
    case 'create_discount': {
      const p = s.params
      const body = {
        type: p.kind, method: p.code ? 'code' : 'automatic', code: p.code, title: p.title, valueType: p.valueType,
        value: p.valueType === 'percent' ? Math.round(p.value * 100) : toMinor(p.value, x.currency),
        config: p.kind === 'product' ? { appliesTo: { type: p.appliesTo.type, ids: p.appliesTo.ids } } : {},
        startsAt: p.startsAt, endsAt: p.endsAt, enabled: true,
      }
      const d = await api.post<{ id?: string }>('/api/admin/discounts', body)
      void x.qc.invalidateQueries({ queryKey: ['discounts'] })
      return { status: 'done', message: `Discount “${p.title}” created`, link: d?.id ? { to: `/discounts/${d.id}`, label: 'View discount' } : undefined }
    }
    case 'bulk_price_change': {
      const vs = await allVariants(s.params.filter)
      const updates = vs.map(v => ({ v, n: repriced(v, s.params.change, x.currency) })).filter(c => c.n)
      const before = updates.map(({ v, n }) => ({ id: v.id, ...(n!.price !== undefined ? { price: v.price } : {}), ...('compareAtPrice' in n! ? { compareAtPrice: v.compareAtPrice } : {}) }))
      for (let i = 0; i < updates.length; i += 1000) {
        await api.patch('/api/admin/variants/bulk', { updates: updates.slice(i, i + 1000).map(({ v, n }) => ({ id: v.id, ...n })) })
      }
      void x.qc.invalidateQueries({ queryKey: ['products'] }); void x.qc.invalidateQueries({ queryKey: ['variants'] })
      return {
        status: 'done', message: `${updates.length.toLocaleString()} prices updated`, link: { to: '/products', label: 'View products' },
        undo: async () => {
          // Each update restores the variant's original price AND compare-at together (a valid pair).
          for (let i = 0; i < before.length; i += 1000) await api.patch('/api/admin/variants/bulk', { updates: before.slice(i, i + 1000) })
          void x.qc.invalidateQueries({ queryKey: ['products'] })
        },
      }
    }
    case 'set_product_status': {
      const action = s.params.status === 'active' ? 'activate' : s.params.status === 'draft' ? 'draft' : 'archive'
      const f = filterParams(s.params.filter)
      const filter = Object.fromEntries(Object.entries({ collection: f.collection, tag: f.tag, ids: f.ids }).filter(([, v]) => v))
      const r = await api.post<{ updated?: number; previous?: Record<string, string> }>('/api/admin/products/bulk', { filter, action })
      void x.qc.invalidateQueries({ queryKey: ['products'] })
      const prev = r?.previous && typeof r.previous === 'object' ? r.previous : null
      return {
        status: 'done', message: `${(r?.updated ?? 0).toLocaleString()} products set to ${s.params.status}`, link: { to: `/products?status=${s.params.status}`, label: 'View products' },
        undo: prev && Object.keys(prev).length ? async () => { await api.post('/api/admin/products/bulk', { action: 'set_status', statuses: prev }); void x.qc.invalidateQueries({ queryKey: ['products'] }) } : undefined,
      }
    }
    case 'create_product_draft': {
      const p = s.params
      let combos: string[][] = [[]]
      for (const o of p.options) combos = combos.flatMap(c => o.values.map(v => [...c, v])).slice(0, 100)
      const r = await api.post<{ id?: string }>('/api/admin/products', {
        title: p.title, bodyHtml: p.description ? p.description.split(/\n\s*\n/).map(t => `<p>${esc(t.trim())}</p>`).join('') : '', status: 'draft',
        options: p.options, variants: combos.map(c => ({ options: c, price: toMinor(p.price, x.currency) })),
      })
      void x.qc.invalidateQueries({ queryKey: ['products'] })
      return { status: 'done', message: `Draft “${p.title}” created`, link: r?.id ? { to: `/products/${r.id}`, label: 'Open product' } : undefined }
    }
    case 'search': return { status: 'done', link: { to: `${LIST_PATH[s.params.entity]}${searchQuery(s)}`, label: `Show ${s.params.entity}` } }
    case 'toggle_payment_method': {
      const r = await api.get<{ configured: any[] }>('/api/admin/payments/providers')
      const row = (Array.isArray(r?.configured) ? r.configured : []).find(p => p?.provider === s.params.provider)
      if (!row) throw new ApiError(404, 'not_found', 'This payment method isn’t set up yet.')
      await withReauth(() => api.patch(`/api/admin/payments/providers/${row.id}`, { enabled: s.params.enabled }))
      void x.qc.invalidateQueries({ queryKey: ['payments'] })
      return { status: 'done', message: `${row.displayName || row.label} turned ${s.params.enabled ? 'on' : 'off'}`, link: { to: '/settings/payments', label: 'Payment settings' } }
    }
    case 'update_announcement': {
      const t = await headerTemplate()
      const base = structuredClone(t.hasDraft && t.draftDoc ? t.draftDoc : t.doc)
      let found = false
      walk(base.nodes, n => { if (!found && n?.type === 'announcement-bar') { found = true; const msgs = Array.isArray(n.props?.messages) ? n.props.messages : []; n.props = { ...n.props, messages: [{ ...(msgs[0] ?? {}), text: s.params.text }] } } })
      if (!found) throw new ApiError(404, 'not_found', 'Your header has no announcement bar.')
      await api.put(`/api/admin/online-store/templates/${t.id}/draft`, { doc: base, baseUpdatedAt: t.updatedAt })
      if (!t.hasDraft) await api.post(`/api/admin/online-store/templates/${t.id}/publish`)
      return { status: 'done', message: t.hasDraft ? 'Saved to your header draft — publish it in the editor' : 'Announcement updated on your store', link: { to: '/online-store/editor', label: 'Open editor' } }
    }
  }
}

// ─── UI ───────────────────────────────────────────────────────────────────────────────────

function AskAiDialog({ text, onClose }: { text: string; onClose: () => void }) {
  const { store, can } = useSession()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [plan, setPlan] = useState<Plan | null>(null)
  const [planError, setPlanError] = useState<unknown>(null)
  const [previews, setPreviews] = useState<Record<string, Preview | 'loading' | { error: string }>>({})
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({})
  const [phase, setPhase] = useState<'planning' | 'review' | 'running' | 'done'>('planning')
  const [attempt, setAttempt] = useState(0)
  const alive = useRef(true)
  useEffect(() => () => { alive.current = false }, [])

  const currency = store?.currency ?? 'USD'
  const ctx = useMemo<Ctx>(() => ({ currency, role: store?.role ?? 'staff', can, collections: new Map() }), [currency, store?.role, can])

  useEffect(() => {
    let off = false
    setPhase('planning'); setPlan(null); setPlanError(null); setPreviews({}); setOutcomes({})
    ;(async () => {
      try {
        const p = await api.post<Plan>('/api/admin/ai/command', { text })
        if (off) return
        if (!isPlan(p)) throw new ApiError(500, 'bad_response', 'The AI returned an unexpected plan.')
        // Collection names for the previews (ids come from the store's own list).
        try {
          const cols = await api.get<{ items: Array<{ id: string; title: string }> }>('/api/admin/collections?limit=250')
          for (const c of Array.isArray(cols?.items) ? cols.items : []) if (typeof c?.id === 'string') ctx.collections.set(c.id, c.title)
        } catch { /* names are cosmetic */ }
        if (off) return
        setPlan(p)
        setPhase('review')
        setPreviews(Object.fromEntries(p.steps.map(s => [s.id, 'loading' as const])))
        await Promise.all(p.steps.map(async s => {
          let pv: Preview | { error: string }
          try { pv = await preview(s, ctx) } catch (e) { pv = { error: errorMessage(e) } }
          if (off) return
          setPreviews(prev => ({ ...prev, [s.id]: pv }))
          if (!('error' in pv) && !pv.blocked) setChosen(prev => new Set(prev).add(s.id))
        }))
      } catch (e) {
        if (!off) setPlanError(e)
      }
    })()
    return () => { off = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, attempt])

  const runnable = plan?.steps.filter(s => chosen.has(s.id)) ?? []
  const stillLoading = Object.values(previews).some(p => p === 'loading')

  const run = async () => {
    if (!plan || !runnable.length || phase !== 'review') return
    setPhase('running')
    const res: Record<string, Outcome> = Object.fromEntries(runnable.map(s => [s.id, { status: 'pending' } as Outcome]))
    setOutcomes({ ...res })
    for (const s of runnable) {
      if (!alive.current) return
      setOutcomes(o => ({ ...o, [s.id]: { status: 'running' } }))
      try {
        const out = await execute(s, { ...ctx, qc })
        setOutcomes(o => ({ ...o, [s.id]: out }))
      } catch (e) {
        setOutcomes(o => ({ ...o, [s.id]: { status: 'error', message: errorMessage(e) } }))
      }
    }
    if (alive.current) setPhase('done')
  }

  const go = (to: string) => { onClose(); navigate(to) }
  const soleNav = phase === 'done' && runnable.length === 1 && (runnable[0].action === 'navigate' || runnable[0].action === 'search') ? outcomes[runnable[0].id]?.link : undefined
  useEffect(() => { if (soleNav) go(soleNav.to) }, [soleNav?.to]) // eslint-disable-line react-hooks/exhaustive-deps

  const failed = Object.values(outcomes).filter(o => o.status === 'error').length
  const footer = phase === 'done'
    ? <Button variant="primary" onClick={onClose}>{failed ? 'Close' : 'Done'}</Button>
    : <>
      <Button onClick={onClose} disabled={phase === 'running'}>Cancel</Button>
      <Button variant="primary" onClick={() => void run()} loading={phase === 'running'} disabled={phase !== 'review' || !runnable.length || stillLoading} icon={<Check />}>
        {runnable.length > 1 ? `Run ${runnable.length} steps` : 'Run'}
      </Button>
    </>

  return (
    <Dialog open onOpenChange={o => { if (!o && phase !== 'running') onClose() }} dismissible={phase !== 'running'} size="lg"
      title={<span className="flex items-center gap-2"><span className="grid size-6 place-items-center rounded-md bg-primary-soft text-primary-strong"><Sparkles className="size-3.5" /></span>Ask AI</span>}
      description={<span className="line-clamp-2">“{text}”</span>} footer={footer}>
      <div className="flex flex-col gap-4" onKeyDown={e => {
        // Keep keys inside this dialog (the palette underneath listens for Enter/arrows).
        e.stopPropagation()
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void run() }
      }}>
        {phase === 'planning' && !planError && (
          <div className="flex flex-col gap-3" role="status" aria-label="Planning">
            <p className="flex items-center gap-2 text-sm text-text-muted"><Loader2 className="size-4 animate-spin" />Working out a plan — nothing changes until you confirm.</p>
            {[0, 1].map(i => <div key={i} className="flex gap-3 rounded-card border border-border p-3"><Skeleton className="size-8" rounded="md" /><div className="flex flex-1 flex-col gap-2"><Skeleton className="h-3.5 w-2/5" /><Skeleton className="h-3 w-4/5" /></div></div>)}
          </div>
        )}
        {planError != null && (
          <Banner tone={planError instanceof ApiError && planError.status === 402 ? 'warning' : 'critical'} title="Couldn’t make a plan"
            actions={planError instanceof ApiError && planError.status === 402
              ? <Button size="sm" onClick={() => go('/settings/plan')}>See plans</Button>
              : <Button size="sm" onClick={() => setAttempt(a => a + 1)}>Try again</Button>}>
            {errorMessage(planError)}
          </Banner>
        )}
        {plan && (
          <>
            <p className="text-base text-text">{plan.summary}</p>
            {plan.warnings.map(w => <Banner key={w} tone="warning">{w}</Banner>)}
            {plan.steps.length === 0 ? (
              <div className="rounded-card border border-dashed border-border-strong px-4 py-6 text-center text-sm text-text-muted">
                Try asking for something like “Create a 15% off code for Diwali until Sunday” or “Show unpaid orders”.
              </div>
            ) : (
              <ol className="flex flex-col gap-2" aria-label="Plan steps">
                {plan.steps.map((s, i) => {
                  const pv = previews[s.id]
                  const out = outcomes[s.id]
                  const blocked = pv && pv !== 'loading' && ('error' in pv || pv.blocked)
                  return (
                    <li key={s.id} className={cn('flex gap-3 rounded-card border bg-surface p-3 transition-colors duration-150', out?.status === 'error' ? 'border-danger/40' : 'border-border', blocked && 'opacity-70')}>
                      <div className="flex flex-col items-center gap-2 pt-0.5">
                        {phase === 'review'
                          ? <Checkbox checked={chosen.has(s.id)} disabled={!!blocked || pv === 'loading'} aria-label={`Include step ${i + 1}`}
                            onCheckedChange={c => setChosen(prev => { const n = new Set(prev); if (c === true) n.add(s.id); else n.delete(s.id); return n })} />
                          : <StepState o={out} />}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-text-subtle [&_svg]:size-4">{ICON[s.action]}</span>
                          <span className="truncate text-base font-medium text-text">{s.description}</span>
                        </div>
                        <div className="flex min-w-0 flex-col gap-0.5 text-sm text-text-muted">
                          {pv === 'loading' || pv === undefined ? <><Skeleton className="h-3 w-3/5" /><Skeleton className="mt-1 h-3 w-2/5" /></>
                            : 'error' in pv ? <span className="flex items-center gap-1.5 text-danger-strong"><CircleAlert className="size-3.5" />{pv.error}</span>
                            : <>
                              {pv.lines.map((l, k) => <div key={k} className="min-w-0 break-words">{l}</div>)}
                              {pv.blocked && <span className="flex items-center gap-1.5 text-warning-strong"><Ban className="size-3.5" />{pv.blocked}</span>}
                              {pv.warn && <span className="flex items-center gap-1.5 text-warning-strong"><CircleAlert className="size-3.5" />{pv.warn}</span>}
                            </>}
                          {out?.message && <span className={cn('mt-1', out.status === 'error' ? 'text-danger-strong' : 'text-success-strong')}>{out.message}</span>}
                        </div>
                        {(out?.link || out?.undo) && out.status === 'done' && (
                          <div className="mt-1 flex flex-wrap gap-2">
                            {out.link && <Button size="sm" variant="secondary" iconRight={<ExternalLink />} onClick={() => go(out.link!.to)}>{out.link.label}</Button>}
                            {out.undo && <UndoButton undo={out.undo} onDone={() => setOutcomes(o => ({ ...o, [s.id]: { ...o[s.id], undo: undefined, message: 'Undone' } }))} />}
                          </div>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
            {phase === 'review' && plan.steps.length > 0 && (
              <p className="flex items-center gap-1.5 text-xs text-text-subtle">Nothing runs until you confirm. <Kbd combo="mod+enter" /> to run.</p>
            )}
          </>
        )}
      </div>
    </Dialog>
  )
}

function StepState({ o }: { o?: Outcome }) {
  if (!o || o.status === 'pending') return <span className="grid size-5 place-items-center rounded-full border border-border-strong" aria-label="Waiting" />
  if (o.status === 'running') return <Loader2 className="size-5 animate-spin text-primary-strong" aria-label="Running" />
  if (o.status === 'done') return <span className="grid size-5 place-items-center rounded-full bg-success text-white" aria-label="Done"><Check className="size-3" strokeWidth={3} /></span>
  if (o.status === 'error') return <span className="grid size-5 place-items-center rounded-full bg-danger text-white" aria-label="Failed"><CircleAlert className="size-3" /></span>
  return <Badge size="sm">Skipped</Badge>
}

function UndoButton({ undo, onDone }: { undo: () => Promise<void>; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  return (
    <Button size="sm" variant="ghost" icon={<Undo2 />} loading={busy} onClick={async () => {
      setBusy(true)
      try { await undo(); toast.success('Change undone'); onDone() } catch (e) { toast.error(e) } finally { setBusy(false) }
    }}>Undo</Button>
  )
}

/** The ⌘K row. Selecting it opens the plan dialog (nested above the palette; closing it closes both). */
export function AskAiCommand({ query, onClose }: { query: string; onClose: () => void }) {
  const [asked, setAsked] = useState<string | null>(null)
  const { can } = useSession()
  const q = query.trim()
  if (!can('ai')) return null
  return (
    <>
      <CommandGroup heading="Ask AI">
        <CommandItem value="ai:ask" keywords={[q]} icon={<Sparkles className="text-primary-strong" />} hint={<span className="hidden sm:inline">Plan · preview · confirm</span>} onSelect={() => setAsked(q)}>
          <span className="truncate">Ask AI: “{q}”</span>
        </CommandItem>
      </CommandGroup>
      {asked && <AskAiDialog text={asked} onClose={() => { setAsked(null); onClose() }} />}
    </>
  )
}
